import React, { useState, useEffect, useMemo, useRef } from 'react'
import { StatsCard } from '../components/StatsCard'
import { QuickActionCard } from '../components/QuickActionCard'
import { ActivityItem } from '../components/ActivityItem'
import { DateRangePresetBar } from '../components/DateRangePresetBar'
import { DivisionStats, useDivision } from '../contexts/DivisionContext'
import {
  BriefcaseIcon,
  CheckCircle2Icon,
  ClockIcon,
  DollarSignIcon,
  UsersIcon,
  HomeIcon,
  CalendarIcon,
  TimerIcon,
  MessageSquareIcon,
  FileTextIcon,
  CheckCircleIcon,
  UserPlusIcon,
  MapPinIcon,
  BellIcon,
  AlertCircleIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { usePermissions } from '../hooks/usePermissions'
import { usesOpsDashboard } from '../lib/fieldHome'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { CrewHomeDashboard } from '../components/dashboard/CrewHomeDashboard'
import { InboxTodayWidget } from '../components/dashboard/InboxTodayWidget'
import { MyDayContent } from '../components/my-day/MyDayContent'
import { useAppShellMode } from '../hooks/useAppShellMode'
import {
  EMPTY_DASHBOARD_STATS,
  fetchDashboardStats,
  peekDashboardStatsCache,
  type DashboardStats,
} from '../api/dashboard'
import {
  formatDateRangeLabel,
  loadDashboardDateRangeSelection,
  resolveDateRangePreset,
  saveDashboardDateRangeSelection,
  type DateRangePresetId,
  type StoredDateRangeSelection,
} from '../lib/dateRangePresets'

export function Dashboard() {
  const { currentDivision } = useDivision()
  const { canSeePricing, can, profile, isSuperAdmin, loading: permLoading } = usePermissions()
  const showOps = usesOpsDashboard(profile?.role, isSuperAdmin)
  const isMobileShell = useAppShellMode() === 'mobile'

  const [rangeSelection, setRangeSelection] = useState<StoredDateRangeSelection>(
    () => loadDashboardDateRangeSelection()
  )
  const range = useMemo(
    () => resolveDateRangePreset(rangeSelection.preset, new Date(), rangeSelection.custom),
    [rangeSelection]
  )
  const rangeLabel = useMemo(() => formatDateRangeLabel(range), [range])

  const [stats, setStats] = useState<DashboardStats>(() => {
    return peekDashboardStatsCache(currentDivision?.id, range) || EMPTY_DASHBOARD_STATS
  })
  const [statsLoading, setStatsLoading] = useState(
    () => !peekDashboardStatsCache(currentDivision?.id, range)
  )
  const [upcomingActivities, setUpcomingActivities] = useState<any[]>([])
  const [recentActivity, setRecentActivity] = useState<any[]>([])
  const statsFetchGen = useRef(0)

  const canLeads = can('leads.view')
  const canProposals = can('proposals.view')
  const canReview = can('review.view')
  const canCustomers = can('customers.view')
  const canProperties = can('properties.view')
  const canBilling = can('financial.billing')
  const canJobs = can('jobs.view')
  const canClock = can('time.clock')
  const canChat = can('communication.chat') || can('communication.inbox')
  const canInbox = can('communication.inbox')

  useEffect(() => {
    saveDashboardDateRangeSelection(rangeSelection)
  }, [rangeSelection])

  useEffect(() => {
    if (permLoading || !showOps) return

    const cached = peekDashboardStatsCache(currentDivision?.id, range)
    if (cached) {
      setStats(cached)
      setStatsLoading(false)
    } else {
      setStatsLoading(true)
    }

    const id = ++statsFetchGen.current
    fetchDashboardStats(
      {
        range,
        division: currentDivision,
        includeJobs: canJobs,
        includeReview: canReview,
        includeRevenue: canSeePricing && canJobs,
        includeLeads: canLeads,
        includeProposals: canProposals,
      },
      (partial) => {
        if (id !== statsFetchGen.current) return
        setStats(partial)
        setStatsLoading(false)
      }
    )
      .then((next) => {
        if (id !== statsFetchGen.current) return
        setStats(next)
      })
      .catch((error) => {
        console.error('Error fetching dashboard stats:', error)
      })
      .finally(() => {
        if (id !== statsFetchGen.current) return
        setStatsLoading(false)
      })

    if (canLeads) fetchUpcomingActivities()
    else setUpcomingActivities([])
    fetchRecentActivity()
  }, [
    currentDivision,
    canSeePricing,
    showOps,
    permLoading,
    canLeads,
    canJobs,
    canReview,
    canProposals,
    range.from,
    range.to,
  ])

  const handlePresetChange = (preset: DateRangePresetId) => {
    setRangeSelection((prev) => {
      if (preset === 'custom' && prev.preset !== 'custom') {
        return {
          preset,
          custom: resolveDateRangePreset(prev.preset, new Date(), prev.custom),
        }
      }
      return { ...prev, preset }
    })
  }

  const fetchUpcomingActivities = async () => {
    try {
      const today = new Date()
      const nextWeek = new Date()
      nextWeek.setDate(today.getDate() + 7)

      const { data, error } = await supabase
        .from('leads')
        .select('id, company_name, contact_first_name, contact_last_name, next_activity_date')
        .gte('next_activity_date', today.toISOString().split('T')[0])
        .lte('next_activity_date', nextWeek.toISOString().split('T')[0])
        .order('next_activity_date', { ascending: true })
        .limit(5)

      if (error) throw error
      setUpcomingActivities(data || [])
    } catch (error) {
      console.error('Error fetching upcoming activities:', error)
    }
  }

  const fetchRecentActivity = async () => {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('id, activity_type, entity_type, description, created_at')
        .order('created_at', { ascending: false })
        .limit(10)

      if (!error && data && data.length > 0) {
        mapActivityData(data)
        return
      }
    } catch {
      // activity_logs table may not exist or be empty
    }

    // Fallback: synthesize recent activity from real entity tables
    try {
      const recentItems: { icon: any; title: string; time: string; color: 'blue' | 'green' | 'yellow' | 'purple'; created_at: string }[] = []

      const fetches: PromiseSettledResult<any>[] = await Promise.allSettled([
        canLeads
          ? supabase.from('leads').select('id, company_name, contact_first_name, contact_last_name, created_at').order('created_at', { ascending: false }).limit(5)
          : Promise.resolve({ data: [] }),
        canJobs
          ? supabase.from('jobs').select('id, title, job_number, status, created_at, updated_at').order('updated_at', { ascending: false }).limit(5)
          : Promise.resolve({ data: [] }),
        canCustomers
          ? supabase.from('customers').select('id, company_name, created_at').order('created_at', { ascending: false }).limit(5)
          : Promise.resolve({ data: [] }),
        canProposals
          ? supabase.from('proposals').select('id, title, proposal_number, status, created_at').order('created_at', { ascending: false }).limit(5)
          : Promise.resolve({ data: [] }),
        can('contracts.view')
          ? supabase.from('contracts').select('id, title, contract_number, status, created_at').order('created_at', { ascending: false }).limit(3)
          : Promise.resolve({ data: [] }),
        canSeePricing
          ? supabase.from('invoices').select('id, invoice_number, total_amount, status, created_at').order('created_at', { ascending: false }).limit(3)
          : Promise.resolve({ data: [] }),
      ])

      const [leadsRes, jobsRes, customersRes, proposalsRes, contractsRes, invoicesRes] = fetches

      if (leadsRes.status === 'fulfilled' && leadsRes.value.data) {
        leadsRes.value.data.forEach((lead: any) => {
          const name = lead.company_name || `${lead.contact_first_name || ''} ${lead.contact_last_name || ''}`.trim()
          recentItems.push({ icon: UserPlusIcon, title: `New lead: ${name}`, time: '', color: 'blue', created_at: lead.created_at })
        })
      }
      if (jobsRes.status === 'fulfilled' && jobsRes.value.data) {
        jobsRes.value.data.forEach((job: any) => {
          const statusLabel = job.status === 'completed' ? 'completed' : job.status === 'in_progress' ? 'started' : 'updated'
          recentItems.push({ icon: BriefcaseIcon, title: `Job ${job.job_number || job.title} ${statusLabel}`, time: '', color: job.status === 'completed' ? 'green' : 'blue', created_at: job.updated_at || job.created_at })
        })
      }
      if (customersRes.status === 'fulfilled' && customersRes.value.data) {
        customersRes.value.data.forEach((c: any) => {
          recentItems.push({ icon: UsersIcon, title: `Customer added: ${c.company_name || 'Unnamed'}`, time: '', color: 'green', created_at: c.created_at })
        })
      }
      if (proposalsRes.status === 'fulfilled' && proposalsRes.value.data) {
        proposalsRes.value.data.forEach((p: any) => {
          recentItems.push({ icon: FileTextIcon, title: `Proposal ${p.proposal_number || p.title} (${p.status})`, time: '', color: 'purple', created_at: p.created_at })
        })
      }
      if (contractsRes.status === 'fulfilled' && contractsRes.value.data) {
        contractsRes.value.data.forEach((c: any) => {
          recentItems.push({ icon: CheckCircleIcon, title: `Contract ${c.contract_number || c.title} created`, time: '', color: 'green', created_at: c.created_at })
        })
      }
      if (invoicesRes.status === 'fulfilled' && invoicesRes.value.data && canSeePricing) {
        invoicesRes.value.data.forEach((inv: any) => {
          recentItems.push({ icon: DollarSignIcon, title: `Invoice ${inv.invoice_number} - $${Number(inv.total_amount || 0).toLocaleString()}`, time: '', color: 'green', created_at: inv.created_at })
        })
      }

      recentItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      const now = new Date()
      const mapped = recentItems.slice(0, 10).map((item) => {
        const createdAt = new Date(item.created_at)
        const diffMs = now.getTime() - createdAt.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)
        let timeAgo = ''
        if (diffMins < 1) timeAgo = 'Just now'
        else if (diffMins < 60) timeAgo = `${diffMins} min ago`
        else if (diffHours < 24) timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
        else timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
        return { icon: item.icon, title: item.title, time: timeAgo, color: item.color }
      })

      setRecentActivity(mapped)
    } catch (err) {
      console.error('Error fetching synthesized activity:', err)
      setRecentActivity([])
    }
  }

  const mapActivityData = (activities: any[]) => {
    const mapped = activities.map((activity) => {
      let icon = BellIcon
      let color: 'blue' | 'green' | 'yellow' | 'purple' = 'blue'

      const activityType = (activity.activity_type || '').toLowerCase()
      const entityType = (activity.entity_type || '').toLowerCase()

      if (activityType.includes('complete') || activityType.includes('finished')) {
        icon = CheckCircleIcon
        color = 'green'
      } else if (activityType.includes('create') || activityType.includes('add')) {
        icon = UserPlusIcon
        color = 'blue'
      } else if (activityType.includes('schedule') || activityType.includes('assign')) {
        icon = MapPinIcon
        color = 'purple'
      } else if (activityType.includes('invoice') || activityType.includes('payment')) {
        icon = CheckCircleIcon
        color = 'green'
      } else if (entityType === 'job') {
        icon = BriefcaseIcon
        color = 'blue'
      } else if (entityType === 'customer' || entityType === 'lead') {
        icon = UsersIcon
        color = 'blue'
      } else if (entityType === 'property') {
        icon = MapPinIcon
        color = 'purple'
      }

      const createdAt = new Date(activity.created_at)
      const now = new Date()
      const diffMs = now.getTime() - createdAt.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      let timeAgo = ''
      if (diffMins < 1) {
        timeAgo = 'Just now'
      } else if (diffMins < 60) {
        timeAgo = `${diffMins} min ago`
      } else if (diffHours < 24) {
        timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else {
        timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      }

      return {
        icon,
        title: activity.description || `${activity.activity_type || 'Activity'} on ${entityType}`,
        time: timeAgo,
        color,
      }
    })

    setRecentActivity(mapped)
  }

  const dashboardStats = useMemo(() => {
    const items: Array<{
      title: string
      value: string | number
      icon: typeof BriefcaseIcon
      color: 'blue' | 'green' | 'yellow' | 'purple'
      subtitle?: string
      loading?: boolean
    }> = []
    const snapshot = 'As of now'

    if (canJobs) {
      items.push(
        {
          title: 'Active Jobs',
          value: stats.activeJobs,
          icon: BriefcaseIcon,
          color: 'blue',
          subtitle: snapshot,
          loading: statsLoading,
        },
        {
          title: 'Completed',
          value: stats.completedInRange,
          icon: CheckCircle2Icon,
          color: 'green',
          subtitle: rangeLabel,
          loading: statsLoading,
        }
      )
    }
    if (canReview && canJobs) {
      items.push({
        title: 'Pending Review',
        value: stats.pendingReview,
        icon: ClockIcon,
        color: 'yellow',
        subtitle: snapshot,
        loading: statsLoading,
      })
    }
    if (canJobs && canSeePricing) {
      items.push({
        title: 'Revenue',
        value: `$${stats.revenueInRange.toLocaleString()}`,
        icon: DollarSignIcon,
        color: 'purple',
        subtitle: rangeLabel,
        loading: statsLoading,
      })
    }
    if (canLeads) {
      items.push({
        title: 'Active Leads',
        value: stats.activeLeads,
        icon: UsersIcon,
        color: 'blue',
        subtitle: snapshot,
        loading: statsLoading,
      })
    }
    if (canProposals) {
      items.push({
        title: 'Pending Proposals',
        value: stats.pendingProposals,
        icon: FileTextIcon,
        color: 'purple',
        subtitle: snapshot,
        loading: statsLoading,
      })
    }
    return items
  }, [stats, statsLoading, rangeLabel, canJobs, canReview, canSeePricing, canLeads, canProposals])

  const quickActions = useMemo(() => {
    const actions = [
      {
        title: 'Customers',
        description: 'Manage customer information',
        icon: UsersIcon,
        href: '/customers',
        color: 'blue' as const,
        show: canCustomers,
      },
      {
        title: 'Properties',
        description: 'Track property locations',
        icon: HomeIcon,
        href: '/properties',
        color: 'green' as const,
        show: canProperties,
      },
      {
        title: 'Jobs',
        description: 'Manage field operations',
        icon: CalendarIcon,
        href: '/jobs',
        color: 'purple' as const,
        show: canJobs,
      },
      {
        title: 'Time Clock',
        description: 'Track employee hours',
        icon: TimerIcon,
        href: '/time-clock',
        color: 'orange' as const,
        show: canClock,
      },
      {
        title: 'Communications',
        description: 'Unified messaging',
        icon: MessageSquareIcon,
        href: can('communication.inbox') ? '/messages?tab=external' : '/messages?tab=internal',
        color: 'pink' as const,
        show: canChat,
      },
      {
        title: 'Billing',
        description: 'Generate invoices',
        icon: FileTextIcon,
        href: '/billing',
        color: 'indigo' as const,
        show: canBilling,
      },
    ]
    return actions.filter((a) => a.show)
  }, [canCustomers, canProperties, canJobs, canClock, canChat, canBilling, can])

  if (permLoading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[320px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </MobilePage>
    )
  }

  if (!showOps) {
    return <CrewHomeDashboard />
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Welcome back"
        subtitle="Here is what is happening with your operations"
      />

      <DateRangePresetBar
        preset={rangeSelection.preset}
        custom={rangeSelection.custom}
        onPresetChange={handlePresetChange}
        onCustomChange={(custom) => setRangeSelection((prev) => ({ ...prev, custom }))}
      />

      {isMobileShell && canClock && (
        <div>
          <p className={mobile.sectionLabel}>Today</p>
          <div className="mt-2">
            <MyDayContent embedded showHeader={false} />
          </div>
        </div>
      )}

      {dashboardStats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-4 lg:gap-6">
          {dashboardStats.map((stat) => (
            <StatsCard key={stat.title} {...stat} />
          ))}
        </div>
      )}

      {canInbox && (
        <InboxTodayWidget userId={profile?.id || null} />
      )}

      {currentDivision && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {currentDivision.name} Overview
          </h2>
          <DivisionStats />
        </div>
      )}

      {canLeads && upcomingActivities.length > 0 && (
        <div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <BellIcon className="w-6 h-6 text-yellow-600" />
              <h2 className="text-xl font-semibold text-gray-900">Upcoming Lead Activities</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              You have {upcomingActivities.length} lead{upcomingActivities.length > 1 ? 's' : ''}{' '}
              with scheduled activities in the next 7 days
            </p>
            <div className="space-y-3">
              {upcomingActivities.map((lead) => {
                const isToday =
                  new Date(lead.next_activity_date).toDateString() === new Date().toDateString()
                const daysUntil = Math.ceil(
                  (new Date(lead.next_activity_date).getTime() - new Date().getTime()) /
                    (1000 * 60 * 60 * 24)
                )

                return (
                  <div
                    key={lead.id}
                    className={`flex items-center justify-between bg-white rounded-lg p-4 border ${
                      isToday ? 'border-red-300' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <AlertCircleIcon
                        className={`w-5 h-5 ${isToday ? 'text-red-500' : 'text-yellow-500'}`}
                      />
                      <div>
                        <p className="font-medium text-gray-900">
                          {lead.company_name ||
                            `${lead.contact_first_name} ${lead.contact_last_name}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          Activity scheduled for{' '}
                          {new Date(lead.next_activity_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {isToday && (
                      <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                        Due Today
                      </span>
                    )}
                    {!isToday && daysUntil <= 3 && (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                        {daysUntil === 1 ? '1 day' : `${daysUntil} days`} away
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {quickActions.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {quickActions.map(({ show: _show, ...action }) => (
              <QuickActionCard key={action.title} {...action} />
            ))}
          </div>
        </div>
      )}

      <div className={`${mobile.surface} p-4 sm:p-6`}>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="divide-y divide-gray-100">
          {recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <ActivityItem key={index} {...activity} />
            ))
          ) : (
            <div className="py-8 text-center text-gray-500">
              No recent activity
            </div>
          )}
        </div>
      </div>
    </MobilePage>
  )
}
